import {
	AccountLevelEnum,
	CarrierPaymentTypeEnum,
	CarriersEnum,
	RatingModeEnum,
	SnackBarVariants,
	USPSPaymentAccountTypeEnum,
	USPSRequestMappingModeEnum,
} from '@app/app.enum';
import * as defaultUserImage from '@app/assets/images/default-user.jpg';
import { CarriersNotRequiringEmail, defaultCountry } from '@app/constants';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { MetaDataService } from '@app/services/meta-data.service';
import { SharedService } from '@app/services/shared.service';
import { isTrue } from '@app/utils';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputAdornment,
	ListItemText,
	MenuItem,
	TextField,
	Typography,
} from '@material-ui/core';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import CustomTooltip from '../shared/custom-tooltip.component';
import Icon from '../shared/icon.component';
import withSnackBar from '../core/message.component';
import { uspsCarrierAccountInfoSchema } from './carriers.schema';
import { AccountService } from '@app/services/account.service';
import CarrierLogoNameComponent from './carrier-logo-name.component';
import * as _ from 'lodash';
import USPSShipConfigComponent from './usps-ship-config.component';
import MRAConfigComponent from './mra-config-component';
import { CarrierAccountService } from '@app/services/carrier-account.service';
import RatingModeComponent from './rating-mode.component';
import classNames from 'classnames';

const SenderInfoFormFactory: models.IUSPSConfiguration = {
	city: '',
	company: '',
	country: defaultCountry,
	state: '',
	street1: '',
	street2: '',
	street3: '',
	zip: '',
	phone: '',
	logo: defaultUserImage,
	email: '',
	name: '',
	mraReturnsMid: '',
	mraAccountCode: '',
	ratingMode: RatingModeEnum.WithShipment,
	printAltReturnAddressOnUSPSLabel: false,
	LabelOwner_CRID: '',
	LabelOwner_MID: '',
	LabelOwner_ManifestMID: '',
	ReturnLabelOwner_CRID: '',
	ReturnLabelOwner_MID: '',
	ReturnLabelOwner_ManifestMID: '',
	MailOwner_MID: '',
	Shipper_MID: '',
	Shipper_CRID: '',
	ConsumerKey: '',
	ConsumerSecret: '',
	Payer_CRID: '',
	Payer_EPSAccountNumber: '',
	Payer_PermitNumber: '',
	Payer_PermitZIP: '',
	Payer_AccountType: USPSPaymentAccountTypeEnum.EPS,
	alternateId: '',
	USPSRequestMappingMode: USPSRequestMappingModeEnum.Normal,
};

const metaDataService: MetaDataService = Injector.get(MetaDataService);
const sharedService: SharedService = Injector.get(SharedService);
const accountService: AccountService = Injector.get(AccountService);
const carrierAccountService: CarrierAccountService = Injector.get(CarrierAccountService);

const USPSConfigComponent: React.FunctionComponent<models.IUSPSePostageProps> = forwardRef(
	(props, ref: React.MutableRefObject<any>) => {
		const formRef: React.MutableRefObject<Formik> = useRef(null);
		const [states, setStates] = useState<models.IState[]>([]);
		const [expandUSPSAccordion, setExpandUSPSAccordion] = useState<boolean>(false);
		let formValues: models.IUSPSConfiguration = null;

		const user = accountService.getUserData();

		const [specialService, setSpecialService] = useState<boolean>(false);

		useImperativeHandle(ref, () => ({
			getFormValues: async () => {
				try {
					await formRef.current.submitForm();
					const errors = Object.keys(formRef.current.state.errors).length;
					if (!errors) {
						return formValues;
					}
					return null;
				} catch (err) {
					throw err;
				}
			},
		}));

		useEffect(() => {
			async function getStates() {
				try {
					const res = await metaDataService.getStates(defaultCountry, props.axiosCancellationToken);
					setStates(res);
				} catch (error) {
					if (!Axios.isCancel(error)) {
						props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
					}
				}
			}

			getStates();
		}, []);

		useEffect(() => {
			async function getDisableSpecialService() {
				try {
					const data = await carrierAccountService.getCarrierSpecialServices('', props.axiosCancellationToken);
					const selectedCarrier: models.IDisableSpeicalService[] = data[props.carrierCode];
					const isReturnSpecialServiceEnabled = selectedCarrier?.find((carrier) => carrier.code === 'Returns')
						? true
						: false;
					setSpecialService(isReturnSpecialServiceEnabled);
				} catch {}
			}

			getDisableSpecialService();
		}, []);

		return (
			<Grid container={true} justifyContent='center' alignItems='center'>
				{props.selectedCarrierLogo && (
					<CarrierLogoNameComponent name={props.selectedCarrierName} logo={props.selectedCarrierLogo} />
				)}
				<Grid item={true} sm={12} md={12} lg={12} xl={12} className='mt-2'>
					<Formik
						initialValues={
							_.isEmpty(props.preloadedData)
								? SenderInfoFormFactory
								: { ...SenderInfoFormFactory, ..._.omitBy(props.preloadedData, _.isUndefined) }
						}
						onSubmit={() => {}}
						validationSchema={uspsCarrierAccountInfoSchema(props.carrierCode, user.account.level)}
						enableReinitialize={true}
						ref={formRef}
					>
						{(formikProps: FormikProps<FormikValues>) => {
							// assigning form values to an outside variable
							formValues = formikProps.values as models.IUSPSConfiguration;

							return (
								<Form noValidate={true} className='width-100'>
									<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12} spacing={2}>
										<Accordion defaultExpanded={true}>
											<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
												<Grid container={true} direction='row' justifyContent='space-evenly'>
													<Grid item={true} xs={12}>
														<Typography variant='h6' color='inherit'>
															{props.intl.formatMessage({
																id: 'app.carrier-account.USPS-shipper-Address-section-header',
															})}
														</Typography>
													</Grid>
												</Grid>
											</AccordionSummary>

											<AccordionDetails style={{ maxHeight: '1000px !important' }}>
												<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='name'
															label={props.intl.formatMessage({
																id: 'app.account.name-label',
															})}
															variant='outlined'
															component={TextField}
															type='text'
															fullWidth={true}
															required={true}
															value={formikProps.values.name}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('name')(e)}
															error={
																!!(
																	(formikProps.values.name.length || formikProps.submitCount) &&
																	!!formikProps.errors.name
																)
															}
															helperText={
																formikProps.values.name.length || formikProps.submitCount
																	? formikProps.errors.name
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='company'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.account.company-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															value={formikProps.values.company}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('company')(e)}
															error={
																!!(
																	(formikProps.values.company.length || formikProps.submitCount) &&
																	!!formikProps.errors.company
																)
															}
															helperText={
																formikProps.values.company.length || formikProps.submitCount
																	? formikProps.errors.company
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='street1'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-street1-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															required={true}
															value={formikProps.values.street1}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('street1')(e)}
															error={
																!!(
																	(formikProps.values.street1.length || formikProps.submitCount) &&
																	!!formikProps.errors.street1
																)
															}
															helperText={
																formikProps.values.street1.length || formikProps.submitCount
																	? formikProps.errors.street1
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='street2'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-street2-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															value={formikProps.values.street2}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('street2')(e)}
															error={
																!!(
																	(formikProps.values.street2.length || formikProps.submitCount) &&
																	!!formikProps.errors.street2
																)
															}
															helperText={
																formikProps.values.street2.length || formikProps.submitCount
																	? formikProps.errors.street2
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='street3'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-street3-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															value={formikProps.values.street3}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('street3')(e)}
															error={
																!!(
																	(formikProps.values.street3.length || formikProps.submitCount) &&
																	!!formikProps.errors.street3
																)
															}
															helperText={
																formikProps.values.street3.length || formikProps.submitCount
																	? formikProps.errors.street3
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='city'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-city-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															required={true}
															value={formikProps.values.city}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('city')(e)}
															error={
																!!(
																	(formikProps.values.city.length || formikProps.submitCount) &&
																	!!formikProps.errors.city
																)
															}
															helperText={
																formikProps.values.city.length || formikProps.submitCount
																	? formikProps.errors.city
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='state'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-state-label',
															})}
															fullWidth={true}
															required={true}
															render={() => (
																<>
																	<TextField
																		variant='outlined'
																		label='State'
																		select={true}
																		required={true}
																		fullWidth={true}
																		value={formikProps.values.state}
																		onChange={(e: React.ChangeEvent<any>) => formikProps.handleChange('state')(e)}
																		SelectProps={{
																			MenuProps: {
																				PaperProps: {
																					style: {
																						maxHeight: '550px',
																					},
																				},
																			},
																			renderValue: (selected) =>
																				states.length
																					? states.find((x: models.IState) => x.code === selected).displayName
																					: null,
																		}}
																		error={
																			!!(
																				(formikProps.values.state.length || formikProps.submitCount) &&
																				!!formikProps.errors.state
																			)
																		}
																	>
																		{states.map((e: models.IState) => (
																			<MenuItem key={e.code} value={e.code}>
																				<ListItemText primary={e.displayName} />
																			</MenuItem>
																		))}
																	</TextField>
																	{!!(
																		(formikProps.values.state.length || formikProps.submitCount) &&
																		!!formikProps.errors.state
																	) && <FormHelperText error={true}>{formikProps.errors.state}</FormHelperText>}
																</>
															)}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='zip'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-zip-label',
															})}
															component={TextField}
															type='text'
															fullWidth={true}
															required={true}
															value={formikProps.values.zip}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('zip')(e)}
															error={
																!!(
																	(formikProps.values.zip.length || formikProps.submitCount) &&
																	!!formikProps.errors.zip
																)
															}
															helperText={
																formikProps.values.zip.length || formikProps.submitCount ? formikProps.errors.zip : null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='country'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-country-label',
															})}
															component={TextField}
															disabled={true}
															type='text'
															fullWidth={true}
															required={true}
															value={formikProps.values.country}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('country')(e)}
															error={
																!!(
																	(formikProps.values.country.length || formikProps.submitCount) &&
																	!!formikProps.errors.country
																)
															}
															helperText={
																formikProps.values.country.length || formikProps.submitCount
																	? formikProps.errors.country
																	: null
															}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='email'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-email-label',
															})}
															component={TextField}
															type='text'
															value={formikProps.values.email}
															required={!CarriersNotRequiringEmail.includes(props.carrierCode)}
															fullWidth={true}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('email')(e)}
															error={
																!!(
																	(formikProps.values.email.length || formikProps.submitCount) &&
																	!!formikProps.errors.email
																)
															}
															helperText={
																formikProps.values.email.length || formikProps.submitCount
																	? formikProps.errors.email
																	: null
															}
															InputProps={{
																endAdornment: (
																	<InputAdornment position='end'>
																		<CustomTooltip
																			items={JSON.parse(
																				props.intl.formatMessage({
																					id: 'app.carrier-account.config.email.tooltip-message',
																				})
																			)}
																			className='display-inline-block'
																		>
																			<Typography>
																				<Icon name='info-circle' />
																			</Typography>
																		</CustomTooltip>
																	</InputAdornment>
																),
															}}
														/>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='phone'
															variant='outlined'
															label={props.intl.formatMessage({
																id: 'app.profile.company-form.company-address-phone-label',
															})}
															component={TextField}
															type='text'
															value={formikProps.values.phone}
															fullWidth={true}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('phone')(e)}
															error={
																!!(
																	(formikProps.values.phone.length || formikProps.submitCount) &&
																	!!formikProps.errors.phone
																)
															}
															helperText={
																formikProps.values.phone.length || formikProps.submitCount
																	? formikProps.errors.phone
																	: null
															}
														/>
													</Grid>
												</Grid>
											</AccordionDetails>
										</Accordion>

										<Accordion className='mt-2'>
											<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
												<Grid container={true} direction='row' justifyContent='space-evenly'>
													<Grid item={true} xs={12}>
														<Typography variant='h6' color='inherit'>
															{props.intl.formatMessage({
																id: 'app.carrier-account.USPS-Configuration-accordion-label',
															})}
														</Typography>
													</Grid>
												</Grid>
											</AccordionSummary>
											<AccordionDetails style={{ maxHeight: '1000px !important' }}>
												<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
													<Grid container={true} item={true} xs={12} alignItems='center'>
														<Typography
															noWrap={true}
															color={'inherit'}
															variant='body1'
															className={classNames('display-inline-block')}
														>
															{props.intl.formatMessage({
																id: 'app.carrier-account.USPS-Label-Config-section-header',
															})}
														</Typography>
													</Grid>
													{(props.carrierCode === CarriersEnum.USPSShip ||
														props.carrierCode === CarriersEnum.USPSeVSNative) && (
														<Grid item={true} sm={6} className='display-flex' alignItems='center'>
															<FormControlLabel
																control={
																	<Checkbox
																		size='small'
																		name='USPSPrintEntryFacilityNameOnLabel'
																		checked={isTrue(formikProps.values.USPSPrintEntryFacilityNameOnLabel)}
																		onChange={(e) => {
																			formikProps.setFieldValue('USPSPrintEntryFacilityNameOnLabel', e.target.checked);
																		}}
																	/>
																}
																label={props.intl.formatMessage({
																	id: 'app.carrier-account.config.USPSPrintEntryFacilityNameOnLabel-label',
																})}
															/>
															<CustomTooltip
																items={JSON.parse(
																	props.intl.formatMessage({
																		id: 'app.carrier-account.config.USPSPrintEntryFacilityNameOnLabel-label.tooltip-message',
																	})
																)}
																className='display-inline-block'
															>
																<Typography>
																	<Icon name='info-circle' />
																</Typography>
															</CustomTooltip>
														</Grid>
													)}
													<Grid item={true} sm={6} className='display-flex' alignItems='center'>
														<FormControlLabel
															control={
																<Checkbox
																	size='small'
																	name='printAltReturnAddressOnUSPSLabel'
																	checked={isTrue(formikProps.values.printAltReturnAddressOnUSPSLabel)}
																	onChange={(e) => {
																		formikProps.setFieldValue('printAltReturnAddressOnUSPSLabel', e.target.checked);
																	}}
																/>
															}
															label={props.intl.formatMessage({
																id: 'app.carrier-account.config.printAltReturnAddressOnUSPSLabel-label',
															})}
														/>
														<CustomTooltip
															items={JSON.parse(
																props.intl.formatMessage({
																	id: 'app.carrier-account.config.printAltReturnAddressOnUSPSLabel-label.tooltip-message',
																})
															)}
															className='display-inline-block'
														>
															<Typography>
																<Icon name='info-circle' />
															</Typography>
														</CustomTooltip>
													</Grid>

													{props.carrierCode === CarriersEnum.USPSShip && (
														<Grid item={true} sm={6} md={6} lg={6} xl={6}>
															<FormControl fullWidth={true}>
																<TextField
																	select={true}
																	variant='outlined'
																	label={props.intl.formatMessage({
																		id: 'app.carrier-account.config.USPSRequestMappingMode-label',
																	})}
																	fullWidth={true}
																	InputLabelProps={{ shrink: true }}
																	SelectProps={{
																		renderValue: (value: string) => {
																			return props.intl.formatMessage({
																				id: `app.carrier-account.config.USPSRequestMappingMode-${value}-label`,
																			});
																		},
																	}}
																	value={formikProps.values.USPSRequestMappingMode}
																	onChange={(e: React.ChangeEvent) =>
																		formikProps.handleChange('USPSRequestMappingMode')(e)
																	}
																>
																	<MenuItem value={USPSRequestMappingModeEnum.Normal}>
																		{props.intl.formatMessage({
																			id: `app.carrier-account.config.USPSRequestMappingMode-${USPSRequestMappingModeEnum.Normal}-label`,
																		})}
																	</MenuItem>
																	<MenuItem value={USPSRequestMappingModeEnum.HidePhoneEmail}>
																		{props.intl.formatMessage({
																			id: `app.carrier-account.config.USPSRequestMappingMode-${USPSRequestMappingModeEnum.HidePhoneEmail}-label`,
																		})}
																	</MenuItem>
																</TextField>
															</FormControl>
														</Grid>
													)}

													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<Field
															name='alternateId'
															label={props.intl.formatMessage({
																id: 'app.carrier-alternateId-label',
															})}
															variant='outlined'
															component={TextField}
															type='text'
															fullWidth={true}
															value={formikProps.values.alternateId}
															onChange={(e: React.ChangeEvent) => formikProps.handleChange('alternateId')(e)}
															InputProps={{
																endAdornment: (
																	<InputAdornment position='end'>
																		<CustomTooltip
																			items={JSON.parse(
																				props.intl.formatMessage({
																					id: 'app.carrier-account.config.alternateId.tooltip-message',
																				})
																			)}
																			className='display-inline-block'
																		>
																			<Typography>
																				<Icon name='info-circle' />
																			</Typography>
																		</CustomTooltip>
																	</InputAdornment>
																),
															}}
														/>
													</Grid>

													{(props.carrierCode === CarriersEnum.USPSePostage ||
														props.carrierCode === CarriersEnum.USPSeVSNative ||
														props.carrierCode === CarriersEnum.USPSeVS ||
														props.carrierCode === CarriersEnum.USPSShip) && (
														<Grid container={true} item={true}>
															<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
																<Typography
																	noWrap={true}
																	color={'inherit'}
																	variant='body1'
																	className={classNames('display-inline-block')}
																>
																	{props.intl.formatMessage({
																		id: 'app.carrier-account.USPS-Rating-Config-section-header',
																	})}
																</Typography>
																<CustomTooltip
																	items={
																		props.paymentType !== CarrierPaymentTypeEnum.Postpaid
																			? JSON.parse(
																					props.intl.formatMessage({
																						id: 'app.carrier-account.config.ratingMode-label-disabled.tooltip-message',
																					})
																			  )
																			: JSON.parse(
																					props.intl.formatMessage({
																						id: 'app.carrier-account.config.ratingMode-label-enabled.tooltip-message',
																					})
																			  )
																	}
																	className='display-inline-block ml-1'
																>
																	<Typography>
																		<Icon name='info-circle' />
																	</Typography>
																</CustomTooltip>
															</Grid>
															<Grid item={true} sm={6} className='display-flex' alignItems='center'>
																<RatingModeComponent
																	formikProps={formikProps}
																	carrierCode={props.carrierCode}
																	name='ratingMode'
																	disabled={props.paymentType !== CarrierPaymentTypeEnum.Postpaid}
																/>
															</Grid>
														</Grid>
													)}
												</Grid>
											</AccordionDetails>
										</Accordion>
									</Grid>
									{specialService &&
										(props.carrierCode === CarriersEnum.USPSePostage || props.carrierCode === CarriersEnum.USPSeVS) && (
											<Accordion
												expanded={expandUSPSAccordion}
												className='mt-2'
												onChange={() => {
													setExpandUSPSAccordion(!expandUSPSAccordion);
												}}
											>
												<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
													<Grid container={true} direction='row' justifyContent='space-evenly'>
														<Grid item={true} xs={12}>
															<Typography variant='h6' color='inherit'>
																{props.intl.formatMessage({
																	id: 'app.carrier-account.USPS-Returns-accordion-label',
																})}
															</Typography>
														</Grid>
													</Grid>
												</AccordionSummary>
												<AccordionDetails style={{ maxHeight: '1000px !important' }}>
													<Grid
														container={true}
														justifyContent='flex-start'
														alignItems='center'
														spacing={2}
														className='ml-1 mr-1 mb-1'
													>
														<MRAConfigComponent
															formikProps={formikProps}
															paymentType={props.paymentType}
															accountLevel={user.account.level}
														/>
													</Grid>
												</AccordionDetails>
											</Accordion>
										)}

									{props.carrierCode === CarriersEnum.USPSShip && user.account.level === AccountLevelEnum.Admin && (
										<Accordion
											expanded={expandUSPSAccordion}
											className='mt-2'
											onChange={() => {
												setExpandUSPSAccordion(!expandUSPSAccordion);
											}}
										>
											<AccordionSummary expandIcon={<Icon name='chevron-down' />}>
												<Grid container={true} direction='row' justifyContent='space-evenly'>
													<Grid item={true} xs={12}>
														<Typography variant='h6' color='inherit'>
															{props.intl.formatMessage({
																id: 'app.carrier-account.USPS-MID-accordion-label',
															})}
														</Typography>
													</Grid>
												</Grid>
											</AccordionSummary>
											<AccordionDetails style={{ maxHeight: '1000px !important' }}>
												<Grid
													container={true}
													justifyContent='flex-start'
													alignItems='center'
													spacing={2}
													className='ml-1 mr-1 mb-1'
												>
													<USPSShipConfigComponent
														setExpandAccordion={setExpandUSPSAccordion}
														formikProps={formikProps}
													/>
												</Grid>
											</AccordionDetails>
										</Accordion>
									)}
								</Form>
							);
						}}
					</Formik>
				</Grid>
			</Grid>
		);
	}
);

export default withSnackBar(injectIntl(USPSConfigComponent, { withRef: true }));



// WEBPACK FOOTER //
// ./src/components/carriers/usps-config.component.tsx